import * as iOSBridge from "iOSBridge";

/**
 * The user menu, as both the web header (MainLayoutView.tsx) and the iOS
 * app's native header (nativeHeader.ts) show it
 */
export interface UserMenuItem {
  /** The web header's element id */
  id: string;
  title: string;
  /** The web header's icon (a Font Awesome class) */
  icon?: string;
  /** The app's icon (an SF Symbol name) */
  image: string;
  onClick: () => void;
}

export interface UserMenuProps {
  onAccountClick: () => void;
  onAdminClick: () => void;
  onForumClick: () => void;
  onLogoutClick: () => void;
  onProfileClick: () => void;
  onSettingsClick: () => void;
  onUserPasswordClick: () => void;
  showAdminPanel?: boolean;
  user: { hasPassword?: boolean; upgraded?: boolean };
}

/** The menu's items above the line, which Logout follows */
export const items = (
  props: UserMenuProps,
  { app }: { app: boolean }
): UserMenuItem[] => [
  {
    id: "user-profile",
    title: "Profile",
    icon: "icon-user",
    image: "person",
    onClick: props.onProfileClick,
  },
  {
    id: "link-settings",
    title: "Settings",
    icon: "icon-cog",
    image: "gearshape",
    onClick: props.onSettingsClick,
  },
  // Accounts made with Apple or Google sign-in have no password to change
  ...(props.user.hasPassword
    ? [
        {
          id: "user-password",
          title: "Change Password",
          icon: "icon-lock",
          image: "key",
          onClick: props.onUserPasswordClick,
        },
      ]
    : []),
  {
    id: "link-account",
    // Short enough for one line of the app's menu
    title: props.user.upgraded
      ? app
        ? "Subscription"
        : "Premium Subscription"
      : "Go Premium",
    icon: "icon-rocket",
    image: "star",
    onClick: props.onAccountClick,
  },
  {
    id: "link-forum",
    title: "Forum",
    icon: "icon-comments",
    image: "bubble.left.and.text.bubble.right",
    onClick: props.onForumClick,
  },
  // The website's footer links to it
  ...(app
    ? [
        {
          id: "link-privacy",
          title: "Privacy Policy",
          image: "hand.raised",
          onClick: () =>
            iOSBridge
              .get()
              ?.openWebPage(`${window.location.origin}/privacyPolicy`),
        },
      ]
    : []),
  ...(props.showAdminPanel
    ? [
        {
          id: "link-admin",
          title: "Admin Panel",
          image: "wrench.and.screwdriver",
          onClick: props.onAdminClick,
        },
      ]
    : []),
];

export const logout = (props: UserMenuProps): UserMenuItem => ({
  id: "user-logout",
  title: "Logout",
  image: "rectangle.portrait.and.arrow.right",
  onClick: props.onLogoutClick,
});
